import { useEffect, useRef } from 'react'
import * as THREE from 'three'

interface BarberPole3DProps {
    style?: React.CSSProperties
}

export function BarberPole3D({ style }: BarberPole3DProps) {
    const mountRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const mount = mountRef.current
        if (!mount) return

        const w = mount.clientWidth
        const h = mount.clientHeight
        if (w === 0 || h === 0) return

        const scene = new THREE.Scene()
        const camera = new THREE.PerspectiveCamera(31, w / h, 0.1, 100)
        camera.position.set(0, 0, 12)

        const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
        renderer.setSize(w, h)
        mount.appendChild(renderer.domElement)

        const group = new THREE.Group()
        scene.add(group)

        // Animated stripe shader
        const poleMat = new THREE.ShaderMaterial({
            uniforms: { u_time: { value: 0 } },
            vertexShader: `
                varying vec2 vUv;
                varying vec3 vNormal;
                void main(){
                    vUv = uv;
                    vNormal = normalize(normalMatrix * normal);
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0);
                }
            `,
            fragmentShader: `
                precision highp float;
                varying vec2 vUv;
                varying vec3 vNormal;
                uniform float u_time;
                void main(){
                    float stripe = vUv.y * 6.0 + vUv.x * 3.0 + u_time * 0.6;
                    float band = mod(stripe, 1.0);
                    vec3 cream = vec3(0.95, 0.92, 0.88);
                    vec3 red   = vec3(1.0, 0.24, 0.24);
                    vec3 blue  = vec3(0.0, 0.53, 1.0);
                    vec3 col;
                    if(band < 0.33) col = cream;
                    else if(band < 0.66) col = red;
                    else col = blue;
                    float light = clamp(dot(vNormal, normalize(vec3(0.5,0.7,1.0))), 0.2, 1.0);
                    col *= 0.55 + 0.6 * light;
                    float fres = pow(1.0 - max(vNormal.z, 0.0), 2.0);
                    col = mix(col, vec3(0.12), fres * 0.35);
                    gl_FragColor = vec4(col, 1.0);
                }
            `,
        })

        // Closed cylinder (false = capped ends, no hard cut)
        const cylinder = new THREE.Mesh(
            new THREE.CylinderGeometry(0.85, 0.85, 4.2, 64, 1, false),
            poleMat
        )
        group.add(cylinder)

        // Chrome caps (wider rings at top/bottom)
        const capMat = new THREE.MeshStandardMaterial({ color: 0xc0c0c0, metalness: 0.7, roughness: 0.3 })
        const capGeo = new THREE.CylinderGeometry(1.0, 1.0, 0.35, 64)
        const capTop = new THREE.Mesh(capGeo, capMat)
        capTop.position.y = 2.25
        const capBot = capTop.clone()
        capBot.position.y = -2.25
        group.add(capTop, capBot)

        // Finial balls
        const ballMat = new THREE.MeshStandardMaterial({ color: 0xd4d4d4, metalness: 0.6, roughness: 0.35 })
        const ballGeo = new THREE.SphereGeometry(0.55, 32, 32)
        const ballTop = new THREE.Mesh(ballGeo, ballMat)
        ballTop.position.y = 2.65
        const ballBot = ballTop.clone()
        ballBot.position.y = -2.65
        group.add(ballTop, ballBot)

        // Lighting
        scene.add(new THREE.AmbientLight(0xffffff, 0.7))
        const dir = new THREE.DirectionalLight(0xffffff, 1.2)
        dir.position.set(3, 5, 4)
        scene.add(dir)
        const rim = new THREE.PointLight(0x0088ff, 0.5, 20)
        rim.position.set(-4, 0, 3)
        scene.add(rim)

        // Mouse parallax
        let mx = 0, my = 0, tx = 0, ty = 0
        const onMove = (e: MouseEvent) => {
            const r = mount.getBoundingClientRect()
            mx = ((e.clientX - r.left) / r.width - 0.5) * 2
            my = ((e.clientY - r.top) / r.height - 0.5) * 2
        }
        window.addEventListener('mousemove', onMove)

        let raf = 0
        const startTime = performance.now()
        const animate = () => {
            const t = (performance.now() - startTime) / 1000
            poleMat.uniforms.u_time.value = t
            tx += (mx - tx) * 0.06
            ty += (my - ty) * 0.06
            group.rotation.y = tx * 0.4 + Math.sin(t * 0.2) * 0.1
            group.rotation.x = -ty * 0.25
            group.position.y = Math.sin(t * 0.5) * 0.08
            renderer.render(scene, camera)
            raf = requestAnimationFrame(animate)
        }
        animate()

        const onResize = () => {
            const nw = mount.clientWidth
            const nh = mount.clientHeight
            if (nw === 0 || nh === 0) return
            camera.aspect = nw / nh
            camera.updateProjectionMatrix()
            renderer.setSize(nw, nh)
        }
        window.addEventListener('resize', onResize)

        return () => {
            cancelAnimationFrame(raf)
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('resize', onResize)
            renderer.dispose()
            if (mount.contains(renderer.domElement)) {
                mount.removeChild(renderer.domElement)
            }
        }
    }, [])

    return <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'absolute', inset: 0, ...style }} />
}
